<template>
  <div class="website-information-form">
    <div class="card-header">
      <h5 class="card-title">
        <i class="fas fa-globe text-indigo-600 mr-2" />
        Website Information
      </h5>
    </div>

    <div
      v-if="loading"
      class="card-body">
      <div class="flex items-center justify-center py-8">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-3xl text-indigo-600 mb-2" />
          <p class="text-sm text-gray-500">
            Loading website information...
          </p>
        </div>
      </div>
    </div>

    <div
      v-else
      class="card-body">
      <!-- View Mode -->
      <div v-if="!isEditing">
        <div
          v-if="hasWebsiteInfo && websiteInformation"
          class="space-y-3">
          <div class="info-grid">
            <div
              v-if="websiteInformation.websiteUrl"
              class="info-item">
              <div class="info-label">
                <i class="fas fa-link mr-1.5" />
                Website URL
              </div>
              <a
                :href="websiteInformation.websiteUrl"
                target="_blank"
                class="info-link">
                {{ websiteInformation.websiteUrl }}
                <i class="fas fa-external-link-alt ml-1 text-xs" />
              </a>
            </div>
            <div
              v-if="websiteInformation.hostingCompany"
              class="info-item">
              <div class="info-label">
                <i class="fas fa-server mr-1.5" />
                Hosting Provider
              </div>
              <div class="info-value">
                {{ websiteInformation.hostingCompany }}
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">
                <i class="fas fa-home mr-1.5" />
                We Host
              </div>
              <div class="info-value">
                <span
                  v-if="websiteInformation.weHost"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <i class="fas fa-check mr-1" />
                  Yes
                </span>
                <span
                  v-else
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  <i class="fas fa-times mr-1" />
                  No
                </span>
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">
                <i class="fas fa-tools mr-1.5" />
                Website Builder Customer
              </div>
              <div class="info-value">
                <span
                  v-if="websiteInformation.websiteBuilderCustomer"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <i class="fas fa-check mr-1" />
                  Yes
                </span>
                <span
                  v-else
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  <i class="fas fa-times mr-1" />
                  No
                </span>
              </div>
            </div>
            <div
              v-if="websiteInformation.loginUrl"
              class="info-item">
              <div class="info-label">
                <i class="fas fa-sign-in-alt mr-1.5" />
                Login URL
              </div>
              <a
                :href="websiteInformation.loginUrl"
                target="_blank"
                class="info-link">
                {{ websiteInformation.loginUrl }}
                <i class="fas fa-external-link-alt ml-1 text-xs" />
              </a>
            </div>
          </div>

          <div
            v-if="websiteInformation.loginUsername || websiteInformation.loginPassword"
            class="credentials-section">
            <div class="credentials-header">
              <i class="fas fa-key mr-1.5" />
              Login Credentials
            </div>
            <div class="credentials-body">
              <div
                v-if="websiteInformation.loginUsername"
                class="credential-item">
                <span class="credential-label">Username:</span>
                <span class="credential-value">{{ websiteInformation.loginUsername }}</span>
              </div>
              <div
                v-if="websiteInformation.loginPassword"
                class="credential-item">
                <span class="credential-label">Password:</span>
                <div class="credential-password">
                  <span
                    v-if="showPassword"
                    class="font-mono">{{ websiteInformation.loginPassword }}</span>
                  <span
                    v-else
                    class="font-mono">••••••••</span>
                  <button
                    class="password-toggle"
                    @click="togglePasswordVisibility">
                    <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="websiteInformation.notes"
            class="notes-section">
            <div class="notes-header">
              <i class="fas fa-sticky-note mr-1.5" />
              Notes
            </div>
            <div class="notes-body">
              {{ websiteInformation.notes }}
            </div>
          </div>
        </div>
        <div
          v-else
          class="empty-state">
          <i class="fas fa-globe-americas empty-state-icon" />
          <p class="empty-state-text">
            No website information available
          </p>
        </div>

        <div class="mt-4">
          <button
            class="action-button action-button-primary"
            @click="startEditing">
            <i class="fas fa-edit mr-1.5" />
            {{ hasWebsiteInfo ? 'Edit' : 'Add Website Information' }}
          </button>
        </div>
      </div>

      <!-- Edit Mode -->
      <form
        v-else
        class="space-y-4"
        @submit.prevent="saveWebsiteInfo">
        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-link mr-1.5" />
            Website URL
          </label>
          <input
            v-model="form.websiteUrl"
            type="url"
            class="form-input"
            :class="{ 'form-input-error': errors.websiteUrl }"
            placeholder="https://example.com">
          <p
            v-if="errors.websiteUrl"
            class="form-error">
            {{ errors.websiteUrl }}
          </p>
        </div>

        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-server mr-1.5" />
            Hosting Company
          </label>
          <input
            v-model="form.hostingCompany"
            type="text"
            class="form-input"
            placeholder="e.g., GoDaddy, Bluehost, AWS">
        </div>

        <div class="form-group">
          <label class="form-label flex items-center">
            <input
              v-model="form.weHost"
              type="checkbox"
              class="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
            <span class="flex items-center">
              <i class="fas fa-home mr-1.5" />
              We Host This Website
            </span>
          </label>
          <p class="text-sm text-gray-500 mt-1">
            Check this if Linda is hosting the client's website
          </p>
        </div>

        <div class="form-group">
          <label class="form-label flex items-center">
            <input
              v-model="form.websiteBuilderCustomer"
              type="checkbox"
              class="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
            <span class="flex items-center">
              <i class="fas fa-tools mr-1.5" />
              Website Builder Customer
            </span>
          </label>
          <p class="text-sm text-gray-500 mt-1">
            Check this if the client is using our website builder service
          </p>
        </div>

        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-sign-in-alt mr-1.5" />
            Login URL
          </label>
          <input
            v-model="form.loginUrl"
            type="url"
            class="form-input"
            :class="{ 'form-input-error': errors.loginUrl }"
            placeholder="https://example.com/admin">
          <p
            v-if="errors.loginUrl"
            class="form-error">
            {{ errors.loginUrl }}
          </p>
        </div>

        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-user mr-1.5" />
            Login Username
          </label>
          <input
            v-model="form.loginUsername"
            type="text"
            class="form-input"
            placeholder="admin">
        </div>

        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-lock mr-1.5" />
            Login Password
          </label>
          <div class="relative">
            <input
              v-model="form.loginPassword"
              :type="showPasswordInput ? 'text' : 'password'"
              class="form-input pr-10"
              placeholder="••••••••">
            <button
              type="button"
              class="password-input-toggle"
              @click="togglePasswordInputVisibility">
              <i :class="showPasswordInput ? 'fas fa-eye-slash' : 'fas fa-eye'" />
            </button>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-sticky-note mr-1.5" />
            Notes
          </label>
          <textarea
            v-model="form.notes"
            rows="3"
            class="form-textarea"
            placeholder="Additional information about the website..." />
        </div>

        <div class="form-actions">
          <div class="flex gap-2">
            <button
              type="submit"
              :disabled="saving"
              class="action-button action-button-success">
              <i
                v-if="saving"
                class="fas fa-spinner fa-spin mr-1.5" />
              <i
                v-else
                class="fas fa-save mr-1.5" />
              Save
            </button>
            <button
              type="button"
              :disabled="saving"
              class="action-button action-button-secondary"
              @click="cancelEditing">
              Cancel
            </button>
          </div>
          <button
            v-if="hasWebsiteInfo"
            type="button"
            :disabled="saving"
            class="action-button action-button-danger"
            @click="deleteWebsiteInfo">
            <i
              v-if="deleting"
              class="fas fa-spinner fa-spin mr-1.5" />
            <i
              v-else
              class="fas fa-trash mr-1.5" />
            Delete
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const loading = ref(true)
const saving = ref(false)
const deleting = ref(false)
const isEditing = ref(false)
const showPassword = ref(false)
const showPasswordInput = ref(false)
const websiteInformation = ref({
  websiteUrl: '',
  hostingCompany: '',
  notes: '',
  loginUrl: '',
  loginUsername: '',
  loginPassword: '',
  weHost: false,
  websiteBuilderCustomer: false
})
const form = ref({
  websiteUrl: '',
  hostingCompany: '',
  notes: '',
  loginUrl: '',
  loginUsername: '',
  loginPassword: '',
  weHost: false,
  websiteBuilderCustomer: false
})
const errors = ref({})

// Computed
const hasWebsiteInfo = computed(() => {
  if (!websiteInformation.value) return false
  return !!(websiteInformation.value.websiteUrl ||
           websiteInformation.value.hostingCompany ||
           websiteInformation.value.notes ||
           websiteInformation.value.loginUrl ||
           websiteInformation.value.loginUsername ||
           websiteInformation.value.loginPassword)
})

// Methods
const fetchWebsiteInformation = async() => {
  try {
    loading.value = true
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/website_information`)
    if (response.data.success) {
      websiteInformation.value = response.data.websiteInformation || {
        websiteUrl: '',
        hostingCompany: '',
        notes: '',
        loginUrl: '',
        loginUsername: '',
        loginPassword: '',
        weHost: false,
        websiteBuilderCustomer: false
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching website information:', error)
    mdtoast.error('Failed to load website information', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

const startEditing = () => {
  form.value = {
    websiteUrl: websiteInformation.value?.websiteUrl || '',
    hostingCompany: websiteInformation.value?.hostingCompany || '',
    notes: websiteInformation.value?.notes || '',
    loginUrl: websiteInformation.value?.loginUrl || '',
    loginUsername: websiteInformation.value?.loginUsername || '',
    loginPassword: websiteInformation.value?.loginPassword || '',
    weHost: websiteInformation.value?.weHost || false,
    websiteBuilderCustomer: websiteInformation.value?.websiteBuilderCustomer || false
  }
  errors.value = {}
  isEditing.value = true
}

const cancelEditing = () => {
  isEditing.value = false
  form.value = {
    websiteUrl: '',
    hostingCompany: '',
    notes: '',
    loginUrl: '',
    loginUsername: '',
    loginPassword: '',
    weHost: false,
    websiteBuilderCustomer: false
  }
  errors.value = {}
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const togglePasswordInputVisibility = () => {
  showPasswordInput.value = !showPasswordInput.value
}

const isValidUrl = (url) => {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

const validateForm = () => {
  errors.value = {}
  let isValid = true

  if (form.value.websiteUrl && !isValidUrl(form.value.websiteUrl)) {
    errors.value.websiteUrl = 'Invalid URL format'
    isValid = false
  }

  if (form.value.loginUrl && !isValidUrl(form.value.loginUrl)) {
    errors.value.loginUrl = 'Invalid URL format'
    isValid = false
  }

  return isValid
}

const saveWebsiteInfo = async() => {
  if (!validateForm()) return

  try {
    saving.value = true
    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/website_information`,
      { website_information: form.value }
    )

    if (response.data.success) {
      websiteInformation.value = response.data.websiteInformation || form.value
      isEditing.value = false
      showPassword.value = false
      mdtoast.success('Website information saved successfully', { duration: 3000 })
    } else {
      mdtoast.error(response.data.error || 'Failed to save website information', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving website information:', error)
    if (error.response?.data?.error) {
      mdtoast.error(error.response.data.error, { duration: 3000 })
    } else {
      mdtoast.error('Failed to save website information', { duration: 3000 })
    }
  } finally {
    saving.value = false
  }
}

const deleteWebsiteInfo = async() => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm('Are you sure you want to delete the website information?')) return

  try {
    deleting.value = true
    const response = await axios.delete(`/ajax/organizations/${props.organizationId}/website_information`)

    if (response.data.success) {
      websiteInformation.value = {
        websiteUrl: '',
        hostingCompany: '',
        notes: '',
        loginUrl: '',
        loginUsername: '',
        loginPassword: '',
        weHost: false,
        websiteBuilderCustomer: false
      }
      isEditing.value = false
      mdtoast.success('Website information deleted successfully', { duration: 3000 })
    } else {
      mdtoast.error(response.data.error || 'Failed to delete website information', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error deleting website information:', error)
    mdtoast.error('Failed to delete website information', { duration: 3000 })
  } finally {
    deleting.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchWebsiteInformation()
})
</script>

<style scoped>
.website-information-form {
  @apply bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden;
}

.card-header {
  @apply px-5 py-4 bg-gray-50 border-b border-gray-200;
}

.card-title {
  @apply text-base font-semibold text-gray-900 flex items-center;
}

.card-body {
  @apply p-5;
}

.info-grid {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.info-item {
  @apply space-y-1;
}

.info-label {
  @apply text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center;
}

.info-value {
  @apply text-sm text-gray-900;
}

.info-link {
  @apply text-sm text-indigo-600 hover:text-indigo-800 hover:underline transition-colors
         inline-flex items-center;
}

.credentials-section {
  @apply bg-gray-50 rounded-lg overflow-hidden;
}

.credentials-header {
  @apply px-4 py-2 bg-gray-100 border-b border-gray-200 text-sm font-medium text-gray-700
         flex items-center;
}

.credentials-body {
  @apply p-4 space-y-2;
}

.credential-item {
  @apply flex items-center gap-2 text-sm;
}

.credential-label {
  @apply font-medium text-gray-600;
}

.credential-value {
  @apply text-gray-900;
}

.credential-password {
  @apply flex items-center gap-2;
}

.password-toggle {
  @apply text-gray-500 hover:text-gray-700 transition-colors p-1;
}

.notes-section {
  @apply bg-gray-50 rounded-lg overflow-hidden;
}

.notes-header {
  @apply px-4 py-2 bg-gray-100 border-b border-gray-200 text-sm font-medium text-gray-700
         flex items-center;
}

.notes-body {
  @apply p-4 text-sm text-gray-700 whitespace-pre-wrap;
}

.empty-state {
  @apply text-center py-8;
}

.empty-state-icon {
  @apply text-4xl text-gray-300 mb-3;
}

.empty-state-text {
  @apply text-sm text-gray-500;
}

.form-group {
  @apply space-y-1;
}

.form-label {
  @apply block text-sm font-medium text-gray-700 flex items-center;
}

.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
         transition-colors duration-200;
}

.form-input-error {
  @apply border-red-500 focus:ring-red-500 focus:border-red-500;
}

.form-textarea {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
         transition-colors duration-200 resize-none;
}

.form-error {
  @apply mt-1 text-xs text-red-600;
}

.password-input-toggle {
  @apply absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700 transition-colors;
}

.form-actions {
  @apply flex items-center justify-between mt-6 pt-4 border-t border-gray-200;
}

.action-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
         disabled:opacity-50 disabled:cursor-not-allowed;
}

.action-button-primary {
  @apply bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500;
}

.action-button-success {
  @apply bg-green-600 text-white hover:bg-green-700 focus:ring-green-500;
}

.action-button-secondary {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500;
}

.action-button-danger {
  @apply bg-red-600 text-white hover:bg-red-700 focus:ring-red-500;
}
</style>
